import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { multipleprintsLinksColumnsVisibility } from "@/config/dashboard/multipleprints";
import { publicAuctionColumnsVisibility } from "@/config/dashboard/public-auction";
import { FormLayoutElement } from "@/config/forms/general";
import { computeFieldMultipleprintRelatedAuthors } from "@/lib/computeFields";
import i18n, { getMainLanguage, SupportedLocale } from "@/i18n";
import { Auction, Multipleprint, MultipleprintInAuction } from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  AuctionLinkGridCard,
  MultipleprintLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import MultipleprintInAuctionDrawer from "./drawer/MultipleprintInAuctionDrawer";

interface ComplexLinkProps {
  id: string;
  multipleprint?: Multipleprint;
  auction?: Auction;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

/**
 * MultipleprintInAuctionLinks component
 * @returns {JSX.Element}
 */
const MultipleprintInAuctionLinks = ({
  id,
  multipleprint,
  auction,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "multipleprintInAuction",
    type,
  );

  const { mutate: deleteMultipleprintInAuction } = useDeleteComplexLink(
    id,
    "multipleprintInAuction",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    async (id: string) => {
      await deleteMultipleprintInAuction({
        id,
        relation: "multipleprintInAuction",
      });
    },
    [deleteMultipleprintInAuction],
  );

  const [searchContent, setSearchContent] = useState("");

  const renderMultipleprintInAuctionRowActions = useCallback(
    (data: MultipleprintInAuction) => {
      const preview =
            type === "auction" && data.multipleprint
              ? {
                  previewType: "multipleprint" as const,
                  previewId: data.multipleprint.id,
                  previewElements: [
                    {
                      label: t("estampesMultiplesDescriptionNumeroEnregistrement"),
                      value:
                        data.multipleprint
                          .estampesMultiplesDescriptionNumeroEnregistrement,
                      type: "text" as const,
                    },
                    {
                      label: t("estampesMultiplesDescriptionResume"),
                      value:
                        data.multipleprint.estampesMultiplesDescriptionResume ??
                        "",
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.multipleprint.attachments ?? [],
                      type: "image" as const,
                    },
                  ],
                }
              : type === "multipleprint" && data.auction
                ? {
                    previewType: "auction" as const,
                    previewId: data.auction.id,
                    previewElements: [
                      {
                        label: t("ventesDescriptionNumeroVente"),
                        value: data.auction.ventesDescriptionNumeroVente,
                        type: "text" as const,
                      },
                      {
                        label: t("ventesDescriptionResume"),
                        value: data.auction.ventesDescriptionResume ?? "",
                        type: "richtext" as const,
                      },
                      {
                        label: t("attachments"),
                        value: data.auction.attachments ?? [],
                        type: "image" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) => (
                <MultipleprintInAuctionDrawer
                  key={data.id}
                  fields={fields}
                  data={data}
                  queryId={id}
                  type={type}
                  open={open}
                  onOpenChange={onOpenChange}
                />
              )}
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<MultipleprintInAuction, any>[] = useMemo(() => {
    return [
      ...(type === "auction"
        ? [
            {
              accessorKey:
                "estampesMultiplesDescriptionNumeroEnregistrement",
              header: t("estampesMultiplesDescriptionNumeroEnregistrement"),
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <TextCell
                  value={
                    row.original.multipleprint?.estampesMultiplesDescriptionNumeroEnregistrement
                  }
                />
              ),
            },
            {
              accessorKey: "multipleprintInAuctionVignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <ImageCell
                  value=""
                  attachments={row.original.multipleprint?.attachments ?? []}
                  domain="multipleprint"
                />
              ),
            },
            {
              accessorKey: "estampesMultiplesDescriptionTitre",
              header: t("estampesMultiplesDescriptionTitre"),
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <HtmlCell
                  value={
                    row.original.multipleprint?.estampesMultiplesDescriptionTitre ?? ""
                  }
                />
              ),
            },
            {
              accessorKey: "author",
              header: t("tab_relatedAuthor"),
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <TextCell
                  value={
                    row.original.multipleprint
                      ? computeFieldMultipleprintRelatedAuthors(
                          row.original.multipleprint,
                          { language: i18n.language as SupportedLocale },
                        )
                      : ""
                  }
                />
              ),
            },
            {
              accessorKey: "estampesMultiplesDescriptionDateDeCreation",
              header: t("tab_estampesMultiplesDescriptionDateDeCreation"),
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <TextCell
                  value={
                    row.original.multipleprint
                      ?.estampesMultiplesDescriptionDateDeCreation
                  }
                />
              ),
            },
            {
              accessorKey: "estampesMultiplesDescriptionResume",
              header: t("estampesMultiplesDescriptionResume"),
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <HtmlCell
                  value={
                    row.original.multipleprint?.estampesMultiplesDescriptionResume ??
                    ""
                  }
                />
              ),
            },
            {
              accessorKey: "ventesDescriptionNumeroVente",
              header: t("ventesDescriptionNumeroVente"),
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <TextCell
                  value={row.original.auction?.ventesDescriptionNumeroVente}
                />
              ),
            },
          ]
        : [
            {
              accessorKey: "ventesDescriptionNumeroVente",
              header: t("ventesDescriptionNumeroVente"),
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <TextCell
                  value={row.original.auction?.ventesDescriptionNumeroVente}
                />
              ),
            },
            {
              accessorKey: "vignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <ImageCell
                  value=""
                  attachments={row.original.auction?.attachments ?? []}
                  domain="auction"
                />
              ),
            },
            {
              accessorKey: "ventesDescriptionCommissaires",
              header: t("ventesDescriptionCommissaires"),
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <TextCell
                  value={row.original.auction?.ventesDescriptionCommissaires}
                />
              ),
            },
            {
              accessorKey: "ventesDescriptionDate",
              header: t("ventesDescriptionDate"),
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <TextCell value={row.original.auction?.ventesDescriptionDate} />
              ),
            },
            {
              accessorKey: "ventesDescriptionVille",
              header: t("ventesDescriptionVille"),
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <TextCell value={row.original.auction?.ventesDescriptionVille} />
              ),
            },
            {
              accessorKey: "ventesDescriptionPays",
              header: t("ventesDescriptionPays"),
              cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
                <TextCell value={row.original.auction?.ventesDescriptionPays} />
              ),
            },
          ]),
      {
        accessorKey: "ventesDansEstampesMultiplesDescriptionNumeroLot",
        header: t("ventesDansEstampesMultiplesDescriptionNumeroLot"),
        cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
          <TextCell
            value={
              row.original.ventesDansEstampesMultiplesDescriptionNumeroLot
            }
          />
        ),
      },
      {
        accessorKey: "ventesDansEstampesMultiplesDescriptionExemplaireVendu",
        header: t("ventesDansEstampesMultiplesDescriptionExemplaireVendu"),
        cell: ({ row }: { row: Row<MultipleprintInAuction> }) => (
          <TextCell
            value={
              row.original.ventesDansEstampesMultiplesDescriptionExemplaireVendu
            }
          />
        ),
      },
      createActionsColumnDef<MultipleprintInAuction>(({ row }) =>
        renderMultipleprintInAuctionRowActions(row.original),
      ),
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  return (
    <div className="w-full">
      {type === "auction" ? (
        <AuctionToMultipleprintLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          auction={auction}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderMultipleprintInAuctionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedMultipleprints")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      ) : (
        <MultipleprintToAuctionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          multipleprint={multipleprint}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderMultipleprintInAuctionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedAuctions")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      )}
    </div>
  );
};

type AuctionToMultipleprintLinksProps =
  LinksTableProps<MultipleprintInAuction> & {
    auction: Auction | undefined;
  };

type MultipleprintInAuctionLinksProps =
  LinksTableProps<MultipleprintInAuction> & {
    multipleprint: Multipleprint | undefined;
  };

/** Vue depuis une vente publique : liste des multiples liés à cette vente */
const AuctionToMultipleprintLinks = ({
  id,
  relatedData,
  fields,
  auction,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: AuctionToMultipleprintLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "auction-links-multipleprint-add";
  const STORAGE_KEY_LINKED = "auction-links-multipleprint-linked";
  const defaultVisibilityAdd = {
    ...multipleprintsLinksColumnsVisibility,
    actions: true,
  };
  const defaultVisibilityLinked = {
    ...multipleprintsLinksColumnsVisibility,
    ...publicAuctionColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityAdd, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityAdd };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityLinked, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityLinked };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notRelatedMultiples } = useGetNotRelatedResource<Multipleprint>({
    resultType: "multiple",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsMultiples: ColumnDef<Multipleprint, any>[] = useMemo(
    () => [
      {
        accessorKey: "estampesMultiplesDescriptionNumeroEnregistrement",
        header: t("estampesMultiplesDescriptionNumeroEnregistrement"),
        cell: ({ row }: { row: Row<Multipleprint> }) => (
          <TextCell
            value={row.original.estampesMultiplesDescriptionNumeroEnregistrement ?? ""}
          />
        ),
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Multipleprint> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="multipleprint"
          />
        ),
      },
      {
        accessorKey: "estampesMultiplesDescriptionTitre",
        header: t("estampesMultiplesDescriptionTitre"),
        cell: ({ row }: { row: Row<Multipleprint> }) => (
          <HtmlCell
            value={row.original.estampesMultiplesDescriptionTitre ?? ""}
          />
        ),
      },
      {
        id: "estampesMultiplesDescriptionTechnique",
        header: t("estampesMultiplesDescriptionTechnique"),
        accessorFn: (row: Multipleprint) =>
          row.estampesMultiplesDescriptionTechnique?.[
            getMainLanguage() as SupportedLocale
          ] ?? "",
        cell: ({ row }: { row: Row<Multipleprint> }) => (
          <TextCell
            value={
              row.original.estampesMultiplesDescriptionTechnique?.[
                getMainLanguage() as SupportedLocale
              ] ?? ""
            }
          />
        ),
      },
      createActionsColumnDef<Multipleprint>(({ row }: { row: Row<Multipleprint> }) => {
          const data = row.original as Multipleprint;
          if (!auction) return null;
          return (
            <LinkDrawerRowActions
              previewType="multipleprint"
              previewId={data.id}
              previewElements={[
                {
                  label: t("estampesMultiplesDescriptionNumeroEnregistrement"),
                  value: data.estampesMultiplesDescriptionNumeroEnregistrement ?? "",
                  type: "text",
                },
                {
                  label: t("estampesMultiplesDescriptionResume"),
                  value: data.estampesMultiplesDescriptionResume ?? "",
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <MultipleprintInAuctionDrawer
                  key={data.id}
                  fields={fields}
                  queryId={id}
                  type="auction"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    auction,
                    multipleprint: data,
                    ventesDansEstampesMultiplesDescriptionNumeroLot: "",
                    ventesDansEstampesMultiplesDescriptionReproduitPage: "",
                    ventesDansEstampesMultiplesDescriptionEstimationHaute: "",
                    ventesDansEstampesMultiplesDescriptionEstimationBasse: "",
                    ventesDansEstampesMultiplesDescriptionVendu: false,
                    ventesDansEstampesMultiplesDescriptionResultatNonListe: false,
                    ventesDansEstampesMultiplesDescriptionNonVendu: false,
                    ventesDansEstampesMultiplesDescriptionNonVenu: false,
                    ventesDansEstampesMultiplesDescriptionRetire: false,
                    ventesDansEstampesMultiplesDescriptionMiseAPrix: "",
                    ventesDansEstampesMultiplesDescriptionPrixMarteau: "",
                    ventesDansEstampesMultiplesDescriptionPrixAvecFrais: "",
                    ventesDansEstampesMultiplesDescriptionExemplaireVendu: "",
                  }}
                />
              )}
            />
          );
        }),
    ],
    [auction, fields, id, t],
  );

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsMultiples}
      data={notRelatedMultiples?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notRelatedMultiples?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <MultipleprintLinkGridCard
                  item={row.multipleprint}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <MultipleprintInAuctionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="auction"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

//Composant pour Oeuvres
const MultipleprintToAuctionLinks = ({
  id,
  relatedData,
  fields,
  multipleprint,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: MultipleprintInAuctionLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "multipleprint-links-auction-add";
  const STORAGE_KEY_LINKED = "multipleprint-links-auction-linked";
  const defaultVisibility = {
    ...publicAuctionColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notAlreadyRelatedAuctions } = useGetNotRelatedResource<Auction>(
    {
      resultType: "auction",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const columnsAuctions: ColumnDef<Auction, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "ventesDescriptionNumeroVente",
        header: t("ventesDescriptionNumeroVente"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionNumeroVente} />;
        },
      },
      {
        accessorKey: "ventesDescriptionTitre",
        header: t("ventesDescriptionTitre"),
        size: 500,
        cell: (props) => {
          return <HtmlCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey: "ventesDescriptionDate",
        header: t("ventesDescriptionDate"),
        size: 100,
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionDate} />;
        },
      },
      {
        accessorKey: "ventesDescriptionVille",
        header: t("ventesDescriptionVille"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionVille} />;
        },
      },
      {
        accessorKey: "ventesDescriptionPays",
        header: t("ventesDescriptionPays"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionPays} />;
        },
      },
      createActionsColumnDef<Auction>(({ row }) => {
          const data = row.original as Auction;
          if (!multipleprint) return null;
          return (
            <LinkDrawerRowActions
              previewType="auction"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("ventesDescriptionNumeroVente"),
                  value: row.original.ventesDescriptionNumeroVente,
                  type: "text",
                },
                {
                  label: t("ventesDescriptionResume"),
                  value: row.original.ventesDescriptionResume,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: row.original.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <MultipleprintInAuctionDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={id}
                  type={"multipleprint"}
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    auction: data,
                    multipleprint: multipleprint,
                    ventesDansEstampesMultiplesDescriptionNumeroLot: "",
                    ventesDansEstampesMultiplesDescriptionReproduitPage: "",
                    ventesDansEstampesMultiplesDescriptionEstimationHaute: "",
                    ventesDansEstampesMultiplesDescriptionEstimationBasse: "",
                    ventesDansEstampesMultiplesDescriptionVendu: false,
                    ventesDansEstampesMultiplesDescriptionResultatNonListe: false,
                    ventesDansEstampesMultiplesDescriptionNonVendu: false,
                    ventesDansEstampesMultiplesDescriptionNonVenu: false,
                    ventesDansEstampesMultiplesDescriptionRetire: false,
                    ventesDansEstampesMultiplesDescriptionMiseAPrix: "",
                    ventesDansEstampesMultiplesDescriptionPrixMarteau: "",
                    ventesDansEstampesMultiplesDescriptionPrixAvecFrais: "",
                    ventesDansEstampesMultiplesDescriptionExemplaireVendu: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [fields, id, multipleprint, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsAuctions}
      data={notAlreadyRelatedAuctions?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelatedAuctions?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <AuctionLinkGridCard
                  item={row.auction}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <MultipleprintInAuctionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="multipleprint"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default MultipleprintInAuctionLinks;
